import { FC, useCallback, MouseEvent } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { useModalControl, ConfirmationModal, Text, useFormField, CheckboxField } from '@frontend/design-system';
import { useTeamChatSelector } from '../../providers/team-chat.provider';

interface HideConversationConfirmationModalProps {
  modalProps: ReturnType<typeof useModalControl>['modalProps'];
  onClickHideConversation: (e?: MouseEvent<HTMLButtonElement>) => Promise<void>;
}

export const HideConversationConfirmationModal: FC<HideConversationConfirmationModalProps> = ({
  modalProps,
  onClickHideConversation,
}) => {
  const { t } = useTranslation('team-chat');
  const noShowFormFieldProps = useFormField({ type: 'checkbox' });
  const { setNoShowHideConversationModal } = useTeamChatSelector((ctx) => ctx.cache);

  const message = (
    <Text css={styles.message}>
      {t(
        'This hides the conversation from all your devices (Desktop and Mobile), but you can always find it again. \n The conversation will reappear automatically on the next message, or you can find it anytime by searching for it via'
      )}
      <span css={styles.messageSubSpan}>
        <Icon name='draft' size={24} css={styles.draftIcon} />
        <Text weight='bold'>{t('New Message.')}</Text>
      </span>
      <CheckboxField
        name={t('No Show checkbox')}
        label={t(`Don’t show this message again`)}
        css={styles.checkbox}
        labelColor='subdued'
        trackingId='team-chat-2.0-skip-hide-confirmation-modal-button'
        {...noShowFormFieldProps}
      />
    </Text>
  );

  const onClickConfirm = useCallback(async () => {
    if (noShowFormFieldProps.value) {
      setNoShowHideConversationModal();
    }
    await onClickHideConversation();
  }, [onClickHideConversation, setNoShowHideConversationModal, noShowFormFieldProps.value]);

  return (
    <ConfirmationModal
      onConfirm={onClickConfirm}
      destructive
      title={t('Hide Conversation')}
      message={message}
      confirmTrackingId='team-chat-2.0-hide-conversation-modal-confirmation-button'
      cancelTrackingId='team-chat-2.0-hide-conversation-modal-cancel-button'
      confirmLabel={t('Hide Conversation')}
      maxWidth={600}
      {...modalProps}
    />
  );
};

const styles = {
  messageSubSpan: css`
    display: inline-flex;
    align-items: center;
    vertical-align: bottom;
  `,
  draftIcon: css`
    margin: ${theme.spacing(0, 0.5)};
  `,
  checkbox: css`
    margin-top: ${theme.spacing(2)};
  `,
  message: css`
    white-space: pre-line;
  `,
};
